import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { TitleBar, LoginTeacherService } from 'kuaidong-service'

import {
  logger,
  UIUtil,
  AppUtil,
  EnvUtil,
  HttpUtil,
  WechatUtil,
  RouterUtil,
  BrowserUtil,
  AppUtilEvent,
} from 'kuaidong-lib'

export function useBasePage(_title?: string) {
  const { route, router, isActive, isUnderApp, _toast, _logData, _logClick, _loading } = useBaseComponent()

  const _wxShareTitle = ref('')
  const _wxShareDesc = ref('')
  const _wxShareImg = ref('')

  if (EnvUtil.isApph5() || EnvUtil.isCampus() || EnvUtil.isAgency()) {
    _setTitleBar()
    _setTitle(_getPageTitle())
  }

  if (EnvUtil.isApph5()) {
    _wxShareTitle.value = '天天跳绳'
    _wxShareDesc.value = '随时随地跳绳，全自动AI计数，跳绳入门训练神器'
    _wxShareImg.value =
      'https://cdn.tiantiantiaosheng.com/dataupload/tool/2021-06-08/dc030dc2-eb3d-480a-a1b0-2586176fb50a/e284643e-a11f-421f-8f2b-0b83f0308a0a.png?x-oss-process=image/resize,h_68,m_lfit'
  } else {
    _wxShareTitle.value = EnvUtil.appNameZHCN()
    _wxShareDesc.value = EnvUtil.appNameDesc()
    _wxShareImg.value =
      'https://cdn.tiantiantiaosheng.com/dataupload/tool/2021-10-20/5284ef74-a4dc-44cc-9575-85405579c81d/a3c271b0-4b6a-455c-ad6a-febb5e1ea497.png?x-oss-process=image/resize,h_68,m_lfit'
  }

  if (route.query.app === 'tzy') {
    BrowserUtil.updateTitle('体智云')
    _wxShareTitle.value = '体智云'
    _wxShareDesc.value = '校园体育智慧云平台'
    _wxShareImg.value =
      'https://cdn.tiantiantiaosheng.com/dataupload/tool/2021-10-20/5284ef74-a4dc-44cc-9575-85405579c81d/a3c271b0-4b6a-455c-ad6a-febb5e1ea497.png?x-oss-process=image/resize,h_68,m_lfit'
  }

  onMounted(() => {
    if (!route.meta?.keepAlive) {
      BrowserUtil.fixIosWhiteScreenBug()
    }

    _logPageVisit()
    _initWechatShare()
  })

  function _setTitle(title: string) {
    BrowserUtil.updateTitle(title)

    if (EnvUtil.isCampus() || EnvUtil.isAgency()) {
      TitleBar.setTitle(title)
    }
  }

  function _setTitleBar() {
    TitleBar.clearBackPath()

    if (_isFromPush()) {
      TitleBar.disableBack()
    }
  }

  function _getPageTitle() {
    return _title || route.meta?.title || TitleBar.defaultTitle
  }

  function _disableTitleBar() {
    TitleBar.disableBack()
  }

  function _initWechatShare() {
    if (BrowserUtil.isWechat()) {
      return WechatUtil.getInstance().then((wx) => {
        wx.setShareConfig(_wxShareTitle.value, _wxShareDesc.value, _wxShareImg.value)
      })
    }
  }

  async function _share(_url = location.href) {
    return AppUtil.shareLink(
      _url,
      _wxShareTitle.value,
      _wxShareDesc.value,
      await HttpUtil.imageProxy(HttpUtil.imageAvatar(_wxShareImg.value, 80, 80))
    )
  }

  async function _getQD() {
    const teacherId = await LoginTeacherService.safeGetTeacherId()

    const key = `__${teacherId}_visite_page_qd__`

    let qd = (route.query?.qd || '') as string
    if (qd) {
      sessionStorage.setItem(key, qd)
    } else {
      qd = sessionStorage.getItem(key) || ''
    }

    return qd
  }

  async function _logPageVisit() {
    const qd = await _getQD()

    if (EnvUtil.isCampus() || EnvUtil.isSaas() || EnvUtil.isApph5()) {
      // Use route name as the event category to group all events for single page.
      logger.event(route.name!, 'visitPage', '访问页面', 1, { qd })
    }
  }

  function _forceAuth() {
    RouterUtil.forceAuth(route, router)
  }

  function _isFromPush() {
    return route.query['from'] === 'push'
  }

  function _isFromWeapp() {
    return route.query['from'] === 'weapp'
  }

  function _isFromAppShare() {
    return route.query['share'] === 'app'
  }

  return {
    route,
    router,
    logger,
    isActive,
    isUnderApp,

    _wxShareTitle,
    _wxShareDesc,
    _wxShareImg,

    _share,
    _toast,
    _getQD,
    _loading,
    _logData,
    _logClick,
    _setTitle,
    _forceAuth,
    _isFromPush,
    _isFromWeapp,
    _logPageVisit,
    _isFromAppShare,
    _disableTitleBar,
    _initWechatShare,
  }
}

export function useBaseComponent() {
  const isActive = ref(true)
  const isUnderApp = ref(false)

  const route = useRoute()
  const router = useRouter()

  AppUtil.on(AppUtilEvent.WebviewActive, () => (isActive.value = true))
  AppUtil.on(AppUtilEvent.WebviewInactive, () => (isActive.value = false))

  function _toast(msg: string) {
    UIUtil.showToast(msg)
  }

  async function _loading(asyncFunc: () => Promise<any>) {
    UIUtil.showLoading()
    try {
      await asyncFunc()
    } finally {
      UIUtil.hideLoading()
    }
  }

  function _logData(_data: string) {
    logger.event(route.name!, 'data', _data)
  }

  function _logClick(_label: string, _value?: number, _data?: string) {
    logger.event(route.name!, 'click', _label, _value, _data)
  }

  onMounted(async () => {
    isUnderApp.value = await AppUtil.isUnderApp()
  })

  return {
    route,
    router,
    isActive,
    isUnderApp,

    _toast,
    _loading,
    _logData,
    _logClick,
  }
}

export const usePage = useBasePage

export const useComponent = useBaseComponent
